'use client';

export type ConsentRegion = 'gdpr' | 'ccpa' | 'none';

export interface ConsentRecord {
  version: '1.0';
  timestamp: string; // ISO 8601
  region: ConsentRegion;
  choices: {
    necessary: true;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
  };
  userAgent: string;
}

const CONSENT_KEY = 'rhinoguard_consent_v1';

/**
 * Detect user's region based on browser locale and timezone
 * No external API calls - client-side only
 */
export function detectRegion(): ConsentRegion {
  if (typeof navigator === 'undefined') return 'gdpr'; // SSR default

  const lang = navigator.language || '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';

  // EU timezone prefixes
  const euTZPrefixes = [
    'Europe/',
    'Atlantic/Azores',
    'Atlantic/Canary',
    'Atlantic/Faroe',
    'Atlantic/Madeira',
    'Arctic/Longyearbyen',
  ];

  // US timezone prefixes
  const usTZPrefixes = ['America/'];

  // EU country codes
  const euCountryCodes = [
    'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
    'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT',
    'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'GB', 'IS', 'LI',
    'NO', 'CH',
  ];

  const isEU =
    euTZPrefixes.some((p) => tz.startsWith(p)) ||
    euCountryCodes.some((c) => lang.toUpperCase().endsWith('-' + c));

  const isUS =
    usTZPrefixes.some((p) => tz.startsWith(p)) &&
    (lang.startsWith('en-US') || lang.startsWith('en-CA'));

  if (isEU) return 'gdpr';
  if (isUS) return 'ccpa';
  return 'none';
}

/**
 * Get consent record from localStorage
 */
export function getConsentRecord(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to parse consent record:', error);
    return null;
  }
}

/**
 * Save consent record to localStorage
 */
export function saveConsentRecord(record: ConsentRecord): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch (error) {
    console.error('Failed to save consent record:', error);
  }
}

/**
 * Create a new consent record
 */
export function createConsentRecord(
  region: ConsentRegion,
  choices: ConsentRecord['choices']
): ConsentRecord {
  return {
    version: '1.0',
    timestamp: new Date().toISOString(),
    region,
    choices: {
      necessary: true,
      analytics: choices.analytics,
      marketing: choices.marketing,
      preferences: choices.preferences,
    },
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
  };
}

/**
 * Check if user has given consent for a specific category
 */
export function hasConsent(category: keyof ConsentRecord['choices']): boolean {
  const record = getConsentRecord();
  if (!record) return false;
  return record.choices[category] === true;
}

/**
 * Log consent action (for analytics/compliance)
 */
export function logConsentAction(
  action: 'accept_all' | 'reject_all' | 'save_preferences' | 'ccpa_dismiss',
  record: ConsentRecord
): void {
  if (typeof window === 'undefined') return;

  try {
    // Store in localStorage for audit trail
    const logs = JSON.parse(localStorage.getItem('rhinoguard_consent_logs') || '[]');
    logs.push({
      action,
      timestamp: new Date().toISOString(),
      record,
    });
    // Keep only last 100 logs
    if (logs.length > 100) logs.shift();
    localStorage.setItem('rhinoguard_consent_logs', JSON.stringify(logs));
  } catch (error) {
    console.error('Failed to log consent action:', error);
  }
}
